import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import InventoryIcon from '@mui/icons-material/Inventory';
import HandshakeIcon from '@mui/icons-material/Handshake';

const MotionCard = motion(Card);

const reasons = [
  {
    icon: <VerifiedIcon />,
    title: 'Quality Assured',
    description: 'We source products from reputable manufacturers ensuring the highest quality standards.',
  },
  {
    icon: <LocalShippingIcon />,
    title: 'Fast Delivery',
    description: 'Quick and reliable delivery service across Riyadh, Dammam, and all of Saudi Arabia.',
  },
  {
    icon: <SupportAgentIcon />,
    title: 'Expert Support',
    description: 'Our technical team provides expert guidance to help you find the right products.',
  },
  {
    icon: <PriceCheckIcon />,
    title: 'Competitive Pricing',
    description: 'Best prices in the market without compromising on product quality.',
  },
  {
    icon: <InventoryIcon />,
    title: 'Wide Inventory',
    description: 'Extensive stock of bearings, seals, belts, tools, and industrial equipment.',
  },
  {
    icon: <HandshakeIcon />,
    title: 'Customer First',
    description: 'Building long-term relationships through trust, reliability, and excellent service.',
  },
];

const WhyChooseUs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: isDark ? 'background.default' : 'white',
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'secondary.main',
              fontWeight: 600,
              letterSpacing: 2,
            }}
          >
            Why Choose Us
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1,
              mb: 2,
            }}
          >
            Your Success is
            <Box component="span" sx={{ color: 'primary.main' }}>
              {' '}Our Priority
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            With years of experience in the industry, we understand what our 
            customers need and deliver excellence every time.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {reasons.map((reason, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  y: -8,
                }}
                sx={{
                  height: '100%',
                  border: isDark ? '1px solid #333' : '1px solid #eee',
                  bgcolor: isDark ? '#1e1e1e' : 'white',
                  borderRadius: 4,
                  overflow: 'visible',
                  boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.05)',
                  '&:hover': {
                    borderColor: '#e57373',
                    boxShadow: '0 20px 40px rgba(229, 115, 115, 0.15)',
                    '& .reason-icon': {
                      bgcolor: '#e57373',
                      color: 'white',
                      transform: 'scale(1.1) rotate(5deg)',
                      boxShadow: '0 10px 20px rgba(229, 115, 115, 0.4)',
                    },
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <Box
                    className="reason-icon"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      bgcolor: isDark ? 'rgba(229, 115, 115, 0.15)' : '#ffebee',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      color: '#e57373',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      '& svg': {
                        fontSize: 40,
                      },
                    }}
                  >
                    {reason.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                    }}
                  >
                    {reason.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                    }}
                  >
                    {reason.description}
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;
